import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import client from '@/api/client';
import type { Tenant, Room, Bed } from '@/api/types';
import { Move, X } from 'lucide-react';
import { Select } from '@/components/ui/Select';

export function TransferBedPanel({ tenant, hostelId, onClose, onSuccess, bare }: {
  tenant: Tenant;
  hostelId: string;
  onClose: () => void;
  onSuccess?: () => void;
  /** Render as plain content without the card border/header/close icon — for use as a page's main content rather than an inline dismissible panel. */
  bare?: boolean;
}) {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const [selectedRoomId, setSelectedRoomId] = useState('');
  const [selectedBedId, setSelectedBedId] = useState('');

  // Load all rooms (for names) and all vacant beds in one request each
  const { data: rooms = [], isLoading: roomsLoading } = useQuery<Room[]>({
    queryKey: ['rooms-all', hostelId],
    queryFn: () => client.get(`/api/rooms?hostelId=${hostelId}`).then(r => r as unknown as Room[]),
  });

  const { data: vacantBeds = [], isLoading: bedsLoading } = useQuery<Bed[]>({
    queryKey: ['vacant-beds', hostelId],
    queryFn: () => client.get(`/api/rooms/beds?hostelId=${hostelId}`).then(r => r as unknown as Bed[]),
  });

  // Rooms that have at least one vacant bed, excluding the tenant's current room
  const vacantRoomIds = new Set(vacantBeds.map(b => b.roomId));
  const availableRooms = rooms.filter(r =>
    r.status === 'ACTIVE' &&
    vacantRoomIds.has(r.id) &&
    r.id !== tenant.bedId  // exclude current room if bedId === roomId (won't match but safe)
  );

  // Beds in the selected room that are vacant
  const bedsForRoom = vacantBeds.filter(b => b.roomId === selectedRoomId);

  const transfer = useMutation({
    mutationFn: () => client.patch(`/api/tenants/${tenant.id}/transfer`, { bedId: selectedBedId }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['tenant', tenant.id] });
      void qc.invalidateQueries({ queryKey: ['tenants'] });
      void qc.invalidateQueries({ queryKey: ['beds'] });
      void qc.invalidateQueries({ queryKey: ['bed-tenant'] });
      void qc.invalidateQueries({ queryKey: ['vacant-beds', hostelId] });
      void qc.invalidateQueries({ queryKey: ['owner-dashboard'] });
      onSuccess?.();
      onClose();
    },
  });

  const loading = roomsLoading || bedsLoading;

  return (
    <div className={bare ? '' : 'bg-blue-50 border border-blue-200 rounded-xl p-4'}>
      {!bare && (
        <div className="flex items-center justify-between mb-3">
          <h3 className="font-semibold text-blue-900 flex items-center gap-2">
            <Move size={15} className="text-blue-600" />
            {t('owner.transferToDifferentRoom', 'Transfer to Different Room')}
          </h3>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600"><X size={16} /></button>
        </div>
      )}

      {loading ? (
        <p className="text-sm text-gray-400 py-2">{t('owner.loadingAvailableRooms', 'Loading available rooms…')}</p>
      ) : availableRooms.length === 0 ? (
        <p className="text-sm text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2">
          {t('owner.noOtherRoomsVacant', 'No other rooms have vacant beds available right now.')}
        </p>
      ) : (
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">
              {t('owner.availableRoom', 'Available Room')} <span className="text-gray-400">{t('owner.withVacantBeds', '({{count}} with vacant beds)', { count: availableRooms.length })}</span>
            </label>
            <Select
              value={selectedRoomId}
              onChange={e => { setSelectedRoomId(e.target.value); setSelectedBedId(''); }}
              options={[
                { value: '', label: t('owner.chooseRoom', '— Choose room —') },
                ...availableRooms.map(r => {
                  const count = vacantBeds.filter(b => b.roomId === r.id).length;
                  return {
                    value: r.id,
                    label: t('owner.roomVacantCount', 'Room {{room}}{{name}} ({{count}} vacant)', { room: r.roomNumber, name: r.roomName ? ` — ${r.roomName}` : '', count }),
                  };
                }),
              ]}
            />
          </div>

          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">
              {t('owner.availableBed', 'Available Bed')}
            </label>
            <Select
              value={selectedBedId}
              onChange={e => setSelectedBedId(e.target.value)}
              disabled={!selectedRoomId}
              options={[
                { value: '', label: t('owner.chooseBed', '— Choose bed —') },
                ...bedsForRoom.map(b => ({ value: b.id, label: `${t('common.bed')} ${b.bedLabel}` })),
              ]}
            />
          </div>
        </div>
      )}

      <div className="flex items-center gap-3 mt-3">
        <button
          onClick={() => transfer.mutate()}
          disabled={!selectedBedId || transfer.isPending}
          className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-1.5 rounded-lg text-sm font-medium disabled:opacity-50"
        >
          {transfer.isPending ? t('owner.transferring', 'Transferring…') : t('owner.confirmTransfer', 'Confirm Transfer')}
        </button>
        <button onClick={onClose} className="border px-4 py-1.5 rounded-lg text-sm text-gray-600">{t('common.cancel')}</button>
        {transfer.isError && (
          <p className="text-red-600 text-sm">{(transfer.error as Error)?.message}</p>
        )}
      </div>
    </div>
  );
}
